import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, User, Clock, Tag, Search, Upload, Calendar, Layout, X, ChevronDown, Plus, Trash2 } from 'lucide-react';
import ICAL from 'ical.js';
import { initiateCanvasLogin } from '../utils/canvasApi';
import { motion, AnimatePresence } from 'framer-motion';
import ScheduleBanner from './ScheduleBanner';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';

const getTimeFromString = (timeStr) => {
  const [time, period] = timeStr.trim().split(' ');
  const [hours, minutes] = time.split(':');
  let hour = parseInt(hours);
  
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  
  return { hour, minutes: parseInt(minutes) };
};

const getCurrentSemesterInfo = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth();

  let semester;
  if (month >= 0 && month <= 4) {
    semester = 'Spring';
  } else if (month >= 5 && month <= 7) {
    semester = 'Summer';
  } else {
    semester = 'Fall';
  }

  return { semester, year };
};

const formatSchedule = (schedule) => {
  if (!schedule) return 'No schedule set';
  try {
    const scheduleData = typeof schedule === 'string' ? JSON.parse(schedule) : schedule;
    return scheduleData.raw || scheduleData.displayString || 'No schedule set';
  } catch (error) {
    console.error('Error parsing schedule:', error);
    return 'Invalid schedule format';
  }
};

const ClassList = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [nextClass, setNextClass] = useState(null);
  const [showBanner, setShowBanner] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [sharedClasses, setSharedClasses] = useState([]);
  
  const currentSemester = getCurrentSemesterInfo();
  const [selectedSemester, setSelectedSemester] = useState(currentSemester);
  const [availableSemesters, setAvailableSemesters] = useState([currentSemester]);

  useEffect(() => {
    let isMounted = true;

    const fetchClasses = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams({
          semester: selectedSemester?.semester || '',
          year: selectedSemester?.year || ''
        }).toString();
        
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes?${queryParams}`
        );
        const data = await response.json();
        
        if (isMounted) {
          setClasses(data);
          setFilteredClasses(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching classes:', error);
          setError('Failed to load classes. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchClasses();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, isSignedIn, selectedSemester]);

  useEffect(() => {
    const filtered = classes.filter(cls =>
      cls.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.professor.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClasses(filtered);
  }, [classes, searchTerm]);

  useEffect(() => {
    const updateNextClass = () => {
      const next = getNextClass(classes);
      setNextClass(next);
    };

    if (classes.length > 0) {
      updateNextClass();
      const interval = setInterval(updateNextClass, 60000);
      return () => clearInterval(interval);
    }
  }, [classes]);

  useEffect(() => {
    const fetchSharedClasses = async () => {
      if (activeTab === 'shared') {
        try {
          const response = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/shared'
          );
          const data = await response.json();
          console.log('Shared classes:', data);
          setSharedClasses(data);
        } catch (error) {
          console.error('Error fetching shared classes:', error);
        }
      }
    };

    fetchSharedClasses();
  }, [activeTab]);

  useEffect(() => {
    const fetchSemesters = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        // Get all classes without semester filtering
        const response = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes'
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch classes');
        }
        
        const classes = await response.json();
        
        // Extract unique semester/year combinations
        const semesterSet = new Set();
        const validSemesters = [];
        
        classes.forEach(cls => {
          if (cls.semester && cls.year) {
            const key = `${cls.semester}-${cls.year}`;
            if (!semesterSet.has(key)) {
              semesterSet.add(key);
              validSemesters.push({
                semester: cls.semester,
                year: cls.year
              });
            }
          }
        });
        
        // Add current semester if not already present
        const currentSemester = getCurrentSemesterInfo();
        const hasCurrentSemester = validSemesters.some(
          sem => sem.semester === currentSemester.semester && sem.year === currentSemester.year
        );
        
        if (!hasCurrentSemester) {
          validSemesters.unshift(currentSemester);
        }
        
        // Sort semesters
        const sortedSemesters = validSemesters.sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          const semesterOrder = { 'Fall': 3, 'Summer': 2, 'Spring': 1 };
          return semesterOrder[b.semester] - semesterOrder[a.semester];
        });
        
        setAvailableSemesters(sortedSemesters);
        
        // Set initial selected semester
        if (!selectedSemester) {
          const mostRecentSemester = sortedSemesters[0];
          setSelectedSemester(mostRecentSemester || currentSemester);
        }
      } catch (error) {
        console.error('Error fetching semesters:', error);
        setAvailableSemesters([getCurrentSemesterInfo()]);
      }
    };

    fetchSemesters();
  }, [isLoaded, isSignedIn]);

  const handleICalImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImportStatus('Importing classes...');
      const text = await file.text();
      const jcalData = ICAL.parse(text);
      const comp = new ICAL.Component(jcalData);
      const vevents = comp.getAllSubcomponents('vevent');

      const newClasses = {};
      let successCount = 0;
      let errorCount = 0;

      for (const vevent of vevents) {
        try {
          const event = new ICAL.Event(vevent);
          const summary = event.summary;
          
          if (!newClasses[summary]) {
            const classData = {
              title: summary,
              professor: '',
              location: event.location,
              credits: '',
              description: '',
              schedule: {}
            };

            const start = event.startDate.toJSDate();
            const end = event.endDate.toJSDate();
            const dayIndex = start.getDay();
            const days = ['U', 'M', 'T', 'W', 'R', 'F', 'S'];
            const startTime = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            const endTime = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

            classData.schedule[days[dayIndex]] = `${startTime}-${endTime}`;
            newClasses[summary] = classData;
          }
        } catch (error) {
          console.error('Error processing event:', error);
          errorCount++;
        }
      }

      for (const newClass of Object.values(newClasses)) {
        try {
          const response = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-class',
            {
              method: 'POST',
              body: JSON.stringify({
                ...newClass,
                id: Date.now().toString(),
                schedule: formatSchedule(newClass.schedule)
              })
            }
          );
          
          if (!response.ok) throw new Error('Failed to save class');
          successCount++;
        } catch (error) {
          console.error('Error saving class:', error);
          errorCount++;
        }
      }

      setImportStatus(`Import complete. ${successCount} classes added successfully. ${errorCount} failed.`);
      
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes'
      );
      const updatedClasses = await response.json();
      setClasses(updatedClasses);
      
    } catch (error) {
      console.error('Error during import:', error);
      setImportStatus('Import failed. Please try again.');
    }
  };

  if (!isLoaded) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  const handleCanvasImport = () => {
    initiateCanvasLogin();
  };

  const handleClassNavigation = (classId) => {
    navigate(`/class/${classId}/lectures`);
  };

  const getNextClass = (classes) => {
    const now = new Date();
    const currentDay = ['U', 'M', 'T', 'W', 'R', 'F', 'S'][now.getDay()];
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    let nextClass = null;
    let minTimeDiff = Infinity;
    let hasClassesToday = false;

    classes.forEach(cls => {
      if (!cls.schedule) return;
      
      cls.schedule.split(', ').forEach(scheduleItem => {
        const [days, timeRange] = scheduleItem.split(' ');
        
        if (days.includes(currentDay)) {
          hasClassesToday = true;
          const [startTime] = timeRange.split('-');
          const { hour, minutes } = getTimeFromString(startTime);
          
          let timeDiff = (hour - currentHour) * 60 + (minutes - currentMinute);

          if (timeDiff < 0 && timeDiff > -50) {
            const remainingTime = 50 + timeDiff;
            if (remainingTime > 0) {
              nextClass = {
                ...cls,
                inProgress: true,
                timeRemaining: remainingTime
              };
            }
          } else if (timeDiff > 0 && timeDiff < minTimeDiff) {
            minTimeDiff = timeDiff;
            nextClass = {
              ...cls,
              inProgress: false,
              timeUntilStart: timeDiff
            };
          }
        }
      });
    });

    return !hasClassesToday ? { status: 'no-classes' } 
         : !nextClass ? { status: 'classes-over' }
         : nextClass;
  };

  const handleDelete = async (e, classId) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this class? This will also delete all associated lectures.')) {
      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${classId}`,
          { method: 'DELETE' }
        );
        
        if (!response.ok) {
          throw new Error('Failed to delete class');
        }
        
        setClasses(classes.filter(cls => cls.id !== classId));
        setFilteredClasses(filteredClasses.filter(cls => cls.id !== classId));
      } catch (error) {
        console.error('Error deleting class:', error);
        alert('Failed to delete class');
      }
    }
  };

  const renderClasses = () => {
    const classesToRender = activeTab === 'shared' ? sharedClasses : filteredClasses;
    
    return classesToRender.map((cls) => (
      <div
        key={cls.id}
        onClick={() => navigate(`/class/${cls.id}/lectures`)}
        className={`
          p-6 rounded-lg cursor-pointer group relative
          bg-theme hover:bg-secondary
          border border-theme
          transform transition-all duration-200 hover:scale-[1.02]
          theme-transition
        `}
      >
        {cls.is_shared && (
          <div className="text-sm text-accent mb-2">
            {activeTab === 'shared' 
              ? `Shared by: ${cls.shared_by_email || 'Unknown'}`
              : 'Shared class'
            }
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-theme">
            {cls.title}
          </h2>
          <button
            onClick={(e) => handleDelete(e, cls.id)}
            className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-100 hover:text-red-600 text-accent"
          >
            <Trash2 size={18} />
          </button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <User size={16} className="text-accent" />
            <span className="text-accent">
              {cls.professor}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-accent" />
            <span className="text-accent">
              {formatSchedule(cls.schedule)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Tag size={16} className="text-accent" />
            <span className="text-accent">
              {cls.credits} credits
            </span>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="min-h-screen bg-theme theme-transition">
      <ScheduleBanner 
        classes={classes} 
        onNavigate={handleClassNavigation}
      />

      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-theme">
                Your Classes
              </h1>
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/add')}
                className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]"
              >
                <Book size={18} />
                <span className="whitespace-nowrap">Add New Class</span>
              </button>
              <label className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 cursor-pointer bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]">
                <Upload size={18} />
                <span className="whitespace-nowrap">Import from iCal</span>
                <input type="file" accept=".ics" className="hidden" onChange={handleICalImport} />
              </label>
              <button
                onClick={handleCanvasImport}
                className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]"
              >
                <Calendar size={18} />
                <span className="whitespace-nowrap">Import from Canvas</span>
              </button>
            </div>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Search classes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 pl-10 rounded-lg bg-secondary text-theme border border-theme focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
            <Search className="absolute left-3 top-3.5 text-accent" size={18} />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div className="relative w-full sm:w-auto">
              <select
                value={`${selectedSemester?.semester}-${selectedSemester?.year}`}
                onChange={(e) => {
                  if (e.target.value === 'current') {
                    setSelectedSemester(getCurrentSemesterInfo());
                  } else {
                    const [semester, year] = e.target.value.split('-');
                    setSelectedSemester({ semester, year: parseInt(year) });
                  }
                }}
                className="w-full px-4 py-2 pr-10 rounded-lg appearance-none min-w-[200px] bg-secondary text-theme border border-theme focus:outline-none focus:ring-2 focus:ring-primary hover:bg-primary transition-colors"
              >
                <option value="current">Current Semester</option>
                {availableSemesters.map(({ semester, year }) => (
                  semester && year ? (
                    <option 
                      key={`${semester}-${year}`} 
                      value={`${semester}-${year}`}
                    >
                      {`${semester} ${year}`}
                    </option>
                  ) : null
                )).filter(Boolean)}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown size={16} className="text-accent" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {importStatus && (
          <div className="mb-6 p-4 rounded-lg bg-secondary text-theme">
            {importStatus}
          </div>
        )}

        <div className="mb-6">
          <div className="flex space-x-4 border-b border-theme overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-4 text-theme whitespace-nowrap ${activeTab === 'all' ? 'border-b-2 border-primary' : ''}`}
            >
              My Classes
            </button>
            <button
              onClick={() => setActiveTab('shared')}
              className={`py-2 px-4 text-theme whitespace-nowrap ${activeTab === 'shared' ? 'border-b-2 border-primary' : ''}`}
            >
              Shared with Me
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {renderClasses()}
        </div>

        {filteredClasses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lg text-accent">
              {searchTerm 
                ? 'No classes found matching your search.'
                : `No classes found for ${selectedSemester?.semester} ${selectedSemester?.year}. Add a new class to get started!`
              }
            </p>
            <button
              onClick={() => navigate('/add')}
              className="mt-4 px-6 py-2 bg-primary hover:bg-primary-hover text-theme rounded-lg transition-colors"
            >
              <Plus size={18} className="inline-block mr-2" />
              Add New Class
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClassList;